//Login Page - Using firebase Password/Email Login

import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MenuItem, Stack, Typography } from '@mui/material';
import Input from '../ui/forms/fields/input';
import PrimaryButton from '../ui/buttons/primaryButton';
import { registerUser } from '../../services/auth';
import { useAlert } from '../../contexts/alertContext';
import { useNavigate } from 'react-router-dom';
import AuthTemplate from './authTemplate';
import { getVenues } from '../../features/venue/venueSlice';
import Select from '../ui/forms/fields/select';
import { createUserProfile } from '../../services/user';

import { useAuth } from '../../contexts/authContext';
import PhoneNumberInput from '../ui/forms/fields/phoneNumberInput';

const schema = yup.object().shape({
  email: yup.string().email('Invalid email'),
  phone: yup.string().nullable(),
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  clubId: yup.string().required('Please select a club')
});

const UserProfileCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSigningUp, setIsSigningUp] = React.useState(false);
  const { showAlertSuccess, showAlertError } = useAlert();
  const venues = useSelector((state) => state.venue.venues);
  const loadingVenues = useSelector((state) => state.loadingVenues);

  const { authenticated, currentUser } = useAuth();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    dispatch(getVenues());
  }, [dispatch]);

  useEffect(() => {
    if (currentUser) {
      setValue('email', currentUser.email);
      setValue('phone', currentUser.phoneNumber);
    }
  }, [currentUser, setValue]);

  const onSignUp = async (data) => {
    if (!isSigningUp) {
      setIsSigningUp(true);
      try {
        if (authenticated && currentUser) {
          await createUserProfile(currentUser.uid, {
            email: data.email,
            phone: data.phone,
            firstName: data.firstName,
            lastName: data.lastName,
            homeClub: data.clubId,
            clubs: []
          });

          showAlertSuccess('Successfully created a profile!');
          window.location.reload();
        }
      } catch (err) {
        showAlertError('Please check user information try again.');
      }

      setIsSigningUp(false);
    }
  };

  return (
    <AuthTemplate title="Complete your profile" subtitle="Add your details before entering Bowlscout.">
      <Stack spacing={1.5}>
        <Input
          label="First Name"
          variant="outlined"
          autoComplete="given-name"
          {...register('firstName')}
          error={!!errors.firstName}
          helperText={errors.firstName?.message}
        />
        <Input
          label="Last Name"
          variant="outlined"
          autoComplete="family-name"
          {...register('lastName')}
          error={!!errors.lastName}
          helperText={errors.lastName?.message}
        />
        <Input
          label="Email"
          variant="outlined"
          autoComplete="email"
          {...register('email')}
          error={!!errors.email}
          helperText={errors.email?.message}
        />
        <PhoneNumberInput
          control={control}
          name="phone"
          label="Phone Number"
          error={errors.phone ? true : false}
          helperText={errors.phone ? errors.phone.message : ''}
          defaultValue={''}
        />

        {loadingVenues ? (
          <Typography>Loading clubs...</Typography>
        ) : (
          <Select control={control} name="clubId" label="Select a Club">
            {venues.map((venue) => (
              <MenuItem key={venue.id} value={venue.id}>
                {venue.name}
              </MenuItem>
            ))}
          </Select>
        )}

        <PrimaryButton
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          disabled={isSigningUp}
          onClick={() => handleSubmit(onSignUp)()}>
          {isSigningUp ? 'Saving...' : 'Continue'}
        </PrimaryButton>
      </Stack>
    </AuthTemplate>
  );
};

export default UserProfileCreate;
